<template>
  <div class="plot-list">
    <div class="plot-item-container" :class="{ grabbable: !isEditing }" v-dragscroll="!isEditing">
      <div class="plot-item default" :class="{ active: isActive() }" @click="onClickPlot()">
        {{ $t("plotPage.overallPlot") }}
      </div>

      <draggable
        v-model="dataSubPlots"
        :animation="300"
        ghostClass="ghost"
        :disabled="!isEditing"
        class="draggable"
        @end="onDragEnd"
      >
        <div
          v-for="item in dataSubPlots"
          :key="item.plotId"
          class="plot-item"
          :class="{ active: isActive(item.plotId), grabbable: isEditing }"
          @click="onClickPlot(item.plotId)"
        >
          {{ item.name }}
        </div>
      </draggable>
    </div>
    <div class="button-container">
      <template v-if="isEditing">
        <checkbox-marked-circle-icon class="icon done" @click="onClickEdit(false)" />
      </template>
      <template v-else>
        <plus-circle-icon class="icon add" @click="onClickAdd" />
        <playlist-edit-icon class="icon edit" @click="onClickEdit(true)" />
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import PlusCircleIcon from "icons/PlusCircle.vue";
import PlaylistEditIcon from "icons/PlaylistEdit.vue";
import CheckboxMarkedCircleIcon from "icons/CheckboxMarkedCircle.vue";
import draggable from "vuedraggable";
import { SubPlot, UpdateSubPlotSortOrderPayload } from "@/lib/models/plot";
import { Dialog, isBilling } from "@/lib/utils";
import PlotCreateDialog, { PlotCreateDialogProps } from "@/components/ui/PlotCreateDialog.vue";
import clone from "lodash/cloneDeep";
import VueDragscroll from "vue-dragscroll";

Vue.use(VueDragscroll);

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    draggable,
    PlusCircleIcon,
    PlaylistEditIcon,
    CheckboxMarkedCircleIcon,
  },
  props: {
    novelId: String,
    plotId: String,
    subPlots: Array as PropType<SubPlot[]>,
  },
  data() {
    const { subPlots } = this;

    return {
      isEditing: false,
      dataSubPlots: clone(subPlots),
    };
  },
  computed: {
    isActive() {
      return (targetPlotId) => {
        const { plotId } = this;

        if (!targetPlotId && !plotId) {
          return true;
        }
        return plotId === targetPlotId;
      };
    },
  },
  methods: {
    async onClickAdd() {
      const { $store, novelId } = this;

      const hasLimit = !(await isBilling($store));

      const plotCreateDialog = new Dialog(PlotCreateDialog);
      const data: PlotCreateDialogProps = { novelId, hasLimit };
      plotCreateDialog.show(data);
    },
    onClickEdit(status) {
      this.isEditing = status;
    },
    onClickPlot(plotId) {
      const { $router, novelId } = this;

      if (plotId) {
        $router.push({ name: "plot", params: { novelId, plotId } }, () => {});
      } else {
        $router.push({ name: "plot", params: { novelId } }, () => {});
      }
    },
    onDragEnd() {
      const { $store, novelId, dataSubPlots } = this;
      const { dispatch } = $store;

      const order = dataSubPlots.map((subPlot) => subPlot.plotId);
      const payload: UpdateSubPlotSortOrderPayload = {
        novelId,
        order,
      };

      dispatch("plotModule/updateSubPlotSortOrder", payload);
    },
  },
  watch: {
    subPlots: {
      handler(subPlots: SubPlot[]) {
        this.dataSubPlots = clone(subPlots);
      },
      immediate: true,
      deep: true,
    },
  },
});

interface Props {
  novelId: string;
  plotId?: string;
  subPlots: SubPlot[];
}

interface Data {
  isEditing: boolean;
  dataSubPlots: SubPlot[];
}

interface Computed {
  isActive(plotId?: string): boolean;
}

interface Methods {
  onClickAdd(): Promise<void>;
  onClickEdit(status: boolean): void;
  onClickPlot(plotId?: string): void;
  onDragEnd(): void;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.plot-list {
  display: flex;
  align-items: center;
  background-color: var(--color-bg-dark);
  color: var(--color-text-on-fixed-bg);
  height: 100%;

  .plot-item-container {
    display: flex;
    align-items: flex-end;
    overflow: auto;
    height: 100%;

    &.grabbable {
      cursor: grab;

      &:active {
        cursor: grabbing;
      }
    }

    /** スクロールバーを非表示 */
    &::-webkit-scrollbar {
      display: none;
    }

    .plot-item {
      height: 80%;
      max-width: 100px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin: 0 0.5em;
      box-sizing: border-box;
      padding: 0.85em 0.75em 0.75em;
      border-radius: var(--radius-sm) var(--radius-sm) 0 0;
      font-weight: var(--font-weight-bold);
      background-color: #5e6266;
      cursor: default;
      transition: all 0.3s;

      &.default {
        min-width: max-content;
      }

      &.active {
        background-color: #a0a0a0;
        height: 90%;
      }

      &:not(.active):hover {
        background-color: #707070;
        height: 90%;
      }

      &.grabbable {
        cursor: grab;
        animation: shake 0.3s infinite;

        @keyframes shake {
          0% {
            transform: rotateZ(0deg);
          }
          25% {
            transform: rotateZ(2deg);
          }
          50% {
            transform: rotateZ(0deg);
          }
          75% {
            transform: rotateZ(-2deg);
          }
          100% {
            transform: rotateZ(0deg);
          }
        }

        &:active {
          cursor: grabbing;
        }
      }
    }

    .draggable {
      display: flex;
      align-items: flex-end;
      height: 100%;
    }
  }

  .button-container {
    min-width: calc(30px * 2 + 1em);
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0.5em;

    .icon {
      display: flex;
      margin-right: 0.5em;
      cursor: pointer;

      &:last-of-type {
        margin-right: 0;
      }

      ::v-deep svg {
        width: 30px;
        height: 30px;
      }

      &.add {
        ::v-deep svg {
          fill: var(--color-primary);
        }
      }

      &.done {
        ::v-deep svg {
          fill: #5cb85c;
        }
      }
    }
  }
}

/** vue-draggable用のスタイル */
.ghost {
  opacity: 0.5;
}
</style>
